// CLEARWAYY v20.7 — BH series plates, larger header logo + clearwayy.in link
import React, { useState, useCallback, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, AlertTriangle, Check, X, ArrowLeft, Loader2, Shield, Phone, MapPin, Clock, ThumbsUp } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import LandingPage from "@/LandingPage";
import PartnerDashboard from "@/PartnerDashboard";
import { supabase } from "@/supabaseClient";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_minimal-traffic/artifacts/fxot6k74_Clearwayy%20logo.jpeg";

// API base URL - routes through FastAPI backend to avoid CORS issues
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Indian vehicle plate regex — supports standard (AP31AX1234) and BH series (26BH1234AA)
const PLATE_REGEX = /^([A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{1,4}|[0-9]{2}BH[0-9]{4}[A-Z]{1,2})$/;

// Relative time helper
const getRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  if (isNaN(diffMs) || diffMs < 0) return '';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins === 1) return '1 minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return '1 hour ago';
  return `${hrs} hours ago`;
};

// Status Polling Screen - RPC polling every 5s (Realtime disabled for RLS)
const StatusPollingScreen = ({ stickerId, type, onReset }) => {
  const [liveStatus, setLiveStatus] = useState('');
  const [statusTimestamp, setStatusTimestamp] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resolved, setResolved] = useState(false);
  const matchFoundRef = useRef(false);
  const intervalRef = useRef(null);

  // Poll via RPC
  const poll = useCallback(async () => {
    if (matchFoundRef.current) return;
    try {
      const { data, error } = await supabase.rpc('get_sticker', { p_id: stickerId });
      if (error || !data) { console.log('Poll RPC error:', error); return; }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return;

      const live = (row.live_status || '').trim();
      const ts = (row.status_timestamp || '').trim();

      setLiveStatus(live);
      setStatusTimestamp(ts);

      const liveNorm = live.toLowerCase();
      if (liveNorm === 'i am on my way' || liveNorm === 'coming') {
        matchFoundRef.current = true;
        clearInterval(intervalRef.current);
        localStorage.removeItem(`cw_cooldown_${stickerId}`);
      }
    } catch (err) {
      console.log('Poll error:', err);
    }
  }, [stickerId]);

  // Start 5s polling, pause when tab hidden
  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, 5000);

    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(intervalRef.current);
      } else if (!matchFoundRef.current) {
        poll();
        intervalRef.current = setInterval(poll, 5000);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [poll]);

  const handleManualCheck = async () => { await poll(); };

  const handleReset = async () => {
    setIsResetting(true);
    try {
      await supabase.rpc('set_sticker_status', { p_id: stickerId, p_status: 'waiting' });
      supabase.rpc('log_resolution', { p_id: stickerId }).catch(() => {});

      const RESET_WEBHOOK = 'https://hook.eu1.make.com/08nqmdlg7bppx091jt7r74khv5wgkuw7';
      fetch(RESET_WEBHOOK, {
        method: 'POST', mode: 'cors',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: stickerId, action: 'reset', live_status: '', status_timestamp: '' }),
      }).catch(() => {});

      clearInterval(intervalRef.current);
      localStorage.removeItem(`cw_cooldown_${stickerId}`);
      setLiveStatus('');
      setStatusTimestamp('');
      matchFoundRef.current = false;
      setResolved(true);
    } catch (err) {
      console.log('Reset error:', err);
      localStorage.removeItem(`cw_cooldown_${stickerId}`);
      setResolved(true);
    } finally {
      setIsResetting(false);
    }
  };

  const handleExit = () => {
    localStorage.removeItem(`cw_cooldown_${stickerId}`);
    onReset();
  };

  // Resolved end state
  if (resolved) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="w-full max-w-md mx-auto"
      >
        <div
          className="bg-white rounded-3xl p-10 shadow-[0_8px_30px_rgb(0,0,0,0.08)] text-center"
          data-testid="resolved-card"
        >
          {/* Animated checkmark */}
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div
              className="w-24 h-24 rounded-full bg-[#ECFDF5] flex items-center justify-center"
              style={{ animation: 'resolvedPulse 2s ease-in-out infinite' }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 15 }}
              >
                <Check className="w-12 h-12 text-[#059669]" strokeWidth={3} />
              </motion.div>
            </div>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-2xl font-black text-black mb-4"
          >
            Situation Resolved!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-black/50 text-sm leading-relaxed mb-10 px-4"
          >
            Thank you for using Clearwayy. We're glad we could help clear the path for you today. Safe travels!
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            onClick={handleExit}
            data-testid="close-exit-btn"
            className="w-full h-14 rounded-full bg-[#059669] text-white text-lg font-bold tracking-wider hover:bg-[#047857] transition-colors"
          >
            Close & Exit
          </motion.button>
        </div>

        <style>{`
          @keyframes resolvedPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(5,150,105,0.15); }
            50% { box-shadow: 0 0 0 16px rgba(5,150,105,0); }
          }
        `}</style>
      </motion.div>
    );
  }

  const isStandard = type === 'standard';
  const statusValue = liveStatus;
  const liveNorm = liveStatus.trim().toLowerCase();

  // Multi-stage status: yellow (waiting) → blue (seen) → green (on the way / coming)
  const isGreen = liveNorm === 'i am on my way' || liveNorm === 'coming';
  const isBlue = liveNorm === 'owner has seen your message';
  const isWaiting = !isGreen && !isBlue;

  const bannerText = isGreen
    ? (isStandard ? 'Owner is on the way!' : 'Your emergency contact has received the SOS and is responding.')
    : 'Owner is viewing your request...';
  const relativeTime = getRelativeTime(statusTimestamp);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md mx-auto"
    >
      <div
        className="glass-card rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
        data-testid="status-polling-screen"
      >
        {/* Header */}
        <div className="text-center mb-5">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center transition-colors duration-700 ${
            isGreen ? 'bg-[#059669]' : isBlue ? 'bg-[#4F46E5]' : 'bg-black'
          }`} style={!isGreen ? { animation: 'gentlePulse 2.5s ease-in-out infinite' } : {}}>
            {isGreen
              ? <Check className="w-8 h-8 text-white" />
              : isBlue
                ? <Check className="w-8 h-8 text-white" />
                : <Loader2 className="w-8 h-8 text-[#FF6B00] animate-spin" />
            }
          </div>
          <h2 className="text-xl font-bold text-black">
            {isGreen ? 'Owner is on the way!' : isBlue ? 'Message Seen' : (isStandard ? 'Owner Notified' : 'Emergency Alert Sent')}
          </h2>
          <p className="text-black/60 text-sm mt-1">
            {isGreen ? '' : isBlue ? 'Owner opened your notification' : 'Waiting for response...'}
          </p>
        </div>

        {/* Green banner - final celebratory state */}
        <AnimatePresence mode="wait">
        {isGreen && (
          <motion.div
            key="green"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            data-testid="status-response-banner"
            className="bg-[#059669]/10 border-2 border-[#059669] rounded-2xl p-5 mb-5"
          >
            <p className="text-[#065F46] font-bold text-base text-center leading-snug">
              The owner has seen your notification and replied that he is heading to the vehicle right now.
            </p>
            {relativeTime && (
              <div className="flex items-center justify-center gap-1.5 mt-3">
                <Clock className="w-3.5 h-3.5 text-[#065F46]/60" />
                <p className="text-[#065F46]/60 text-xs font-medium">
                  Responded {relativeTime}
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Blue/Indigo banner - intermediate state */}
        {isBlue && (
          <motion.div
            key="blue"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            data-testid="status-seen-banner"
            className="bg-[#4F46E5]/10 border-2 border-[#4F46E5] rounded-2xl p-5 mb-5"
          >
            <p className="text-[#3730A3] font-bold text-base text-center">
              Owner is viewing your request...
            </p>
            <p className="text-[#3730A3]/60 text-xs text-center mt-2">
              Waiting for their confirmation
            </p>
          </motion.div>
        )}

        {/* Yellow waiting - initial state */}
        {isWaiting && (
          <motion.div
            key="waiting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-black/5 rounded-2xl p-4 mb-5 text-center"
          >
            <p className="text-black/50 text-sm">
              Checking for updates...
            </p>
            <button
              onClick={handleManualCheck}
              data-testid="manual-check-btn"
              className="mt-2 text-[#FF6B00] text-xs font-medium hover:underline"
            >
              Check now
            </button>
          </motion.div>
        )}
        </AnimatePresence>

        <style>{`
          @keyframes gentlePulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(0,0,0,0.08); }
            50% { box-shadow: 0 0 0 10px rgba(0,0,0,0); }
          }
        `}</style>

        {/* Thank You / Problem Resolved button */}
        <button
          onClick={handleReset}
          disabled={isResetting}
          data-testid="thank-you-btn"
          className="btn-primary w-full h-14 rounded-full text-lg font-bold uppercase tracking-wider flex items-center justify-center gap-2"
        >
          {isResetting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <ThumbsUp className="w-5 h-5" />
              Problem Resolved
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

// Validation schemas - Full registration form (Columns E-I, O-P)
const stickerRegistrationSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  city: z.string().min(2, "City must be at least 2 characters"),
  phone: z.string().regex(/^[0-9]{10}$/, "Phone must be exactly 10 digits"),
  email: z.string().email("Invalid email address"),
  vehicle_type: z.enum(["car", "bike", "auto"], { required_error: "Please select a vehicle type" }),
  vehicle_number: z.string().transform(v => v.replace(/\s+/g, '').toUpperCase()).pipe(z.string().regex(PLATE_REGEX, "Invalid plate format (e.g., AP31AX1234 or 26BH1234AA)")),
  emergency_name: z.string().min(2, "Emergency contact name is required"),
  emergency_number: z.string().regex(/^[0-9]{10}$/, "Emergency contact must be exactly 10 digits"),
});

// Video Background Component
// Simplified background for QR scan screens
const VideoBackground = () => (
  <div className="fixed inset-0 -z-10 bg-white" />
);

// Header Component — uses logo
const Header = () => (
  <header className="text-center pt-10 pb-6 px-6">
    <img
      src={LOGO_URL}
      alt="Clearwayy"
      className="h-20 w-auto mx-auto mb-2"
      data-testid="header-logo"
    />
    <a href="/" className="text-xs text-black/35 font-medium tracking-wide hover:text-[#FF6B00] transition-colors">
      www.clearwayy.in
    </a>
  </header>
);

// No ID Error Component — "Oops" screen
const NoIdError = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="w-full max-w-md mx-auto"
  >
    <div className="glass-card rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#FF6B00]/10 flex items-center justify-center">
        <AlertTriangle className="w-10 h-10 text-[#FF6B00]" />
      </div>
      <h2 className="text-2xl font-black text-black mb-3">Oops!</h2>
      <p className="text-black/50 text-sm leading-relaxed mb-6">
        This link seems to be incorrect. Please scan a valid Clearwayy QR code.
      </p>
      <a
        href="/"
        data-testid="back-home-btn"
        className="btn-primary inline-block px-8 py-3 rounded-full text-sm font-bold"
      >
        Back to Home
      </a>
    </div>
  </motion.div>
);

// Path Card Component
const PathCard = ({ title, icon: Icon, variant, onClick, testId }) => {
  const isLight = variant === "light";
  
  return (
    <motion.button
      data-testid={testId}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`path-card w-full p-6 rounded-3xl flex items-center gap-4 shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-colors ${
        isLight 
          ? "bg-white text-black hover:bg-gray-50" 
          : "bg-black text-[#FF6B00] hover:bg-[#1a1a1a]"
      }`}
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
        isLight ? "bg-black/5" : "bg-white/10"
      }`}>
        <Icon className="w-7 h-7" strokeWidth={2.5} />
      </div>
      <span className="text-xl font-bold tracking-tight">{title}</span>
    </motion.button>
  );
};

// Sticker Registration Form (New User from QR scan) - Full form with columns E-I, O-P
const StickerRegistrationForm = ({ stickerId, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(stickerRegistrationSchema),
  });

  const handlePlateChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setValue("vehicle_number", value);
  };

  const onSubmit = async (data) => {
    if (isDisabled) return;
    
    setIsSubmitting(true);
    setIsDisabled(true);

    try {
      const formData = {
        id: stickerId,
        action: 'register',
        name: data.name,
        city: data.city,
        phone: `+91${data.phone}`,
        email: data.email,
        vehicle_type: data.vehicle_type,
        vehicle_number: data.vehicle_number,
        emergency_name: data.emergency_name,
        emergency_number: `+91${data.emergency_number}`,
      };


      // 1. Supabase RPC — blocks for UI success
      const { data: rpcResult, error: rpcError } = await supabase.rpc('register_sticker', {
        p_id: stickerId,
        p_name: formData.name,
        p_phone: formData.phone,
        p_type: formData.vehicle_type,
        p_number: formData.vehicle_number,
      });

      if (rpcError) throw rpcError;

      // RPC returns false if already registered
      if (rpcResult === false) {
        toast.error("This sticker is already registered. Redirecting...");
        setTimeout(() => window.location.reload(), 2000);
        return;
      }


      // 2. Async Make.com sheet sync — fire and forget
      const REGISTER_WEBHOOK = 'https://hook.eu1.make.com/08nqmdlg7bppx091jt7r74khv5wgkuw7';
      fetch(REGISTER_WEBHOOK, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(formData),
      }).catch(() => {}); // fire-and-forget
      
      // 3. Success — transition to Notify Owner screen
      const message = `Registration Successful! Your Clearwayy sticker for ${data.vehicle_number} is now active.`;
      onSuccess(message, data.vehicle_number);
    } catch (error) {
      console.log('Registration error:', error);
      toast.error("Connection lost. Please check your internet and try again.");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setIsDisabled(false), 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md mx-auto"
    >
      <div 
        className="glass-card rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)] max-h-[85vh] overflow-y-auto"
        data-testid="sticker-registration-form"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-black flex items-center justify-center">
            <Shield className="w-8 h-8 text-[#FF6B00]" />
          </div>
          <h2 className="text-xl font-bold text-black">Activate Your Sticker</h2>
          <p className="text-black/60 text-sm mt-1">ID: {stickerId}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Column E: Name */}
          <div>
            <Label htmlFor="name" className="text-black font-medium">Full Name</Label>
            <Input
              id="name"
              data-testid="reg-name-input"
              {...register("name")}
              placeholder="John Doe"
              className="input-clearwayy h-12 text-base rounded-xl mt-1"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
          </div>

          {/* Column F: City */}
          <div>
            <Label htmlFor="city" className="text-black font-medium">City</Label>
            <Input
              id="city"
              data-testid="reg-city-input"
              {...register("city")}
              placeholder="Hyderabad"
              className="input-clearwayy h-12 text-base rounded-xl mt-1"
            />
            {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city.message}</p>}
          </div>

          {/* Column G: Phone */}
          <div>
            <Label htmlFor="phone" className="text-black font-medium">WhatsApp Number</Label>
            <div className="relative mt-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-black/60 font-medium text-sm">+91</span>
              <Input
                id="phone"
                data-testid="reg-phone-input"
                {...register("phone")}
                placeholder="9876543210"
                maxLength={10}
                className="input-clearwayy h-12 text-base rounded-xl pl-12"
              />
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
          </div>

          {/* Column H: Email */}
          <div>
            <Label htmlFor="email" className="text-black font-medium">Email</Label>
            <Input
              id="email"
              data-testid="reg-email-input"
              type="email"
              {...register("email")}
              placeholder="john@example.com"
              className="input-clearwayy h-12 text-base rounded-xl mt-1"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
          </div>

          {/* Vehicle Type */}
          <div>
            <Label htmlFor="vehicle_type" className="text-black font-medium">Vehicle Type</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[
                { value: 'car', label: 'Car' },
                { value: 'bike', label: 'Bike' },
                { value: 'auto', label: 'Auto' },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center justify-center h-12 rounded-xl border cursor-pointer transition-all text-sm font-medium ${
                    watch && watch("vehicle_type") === opt.value
                      ? 'bg-[#FF6B00] text-white border-[#FF6B00]'
                      : 'input-clearwayy hover:border-[#FF6B00]/50'
                  }`}
                >
                  <input
                    type="radio"
                    value={opt.value}
                    {...register("vehicle_type")}
                    className="sr-only"
                    data-testid={`reg-vehicle-type-${opt.value}`}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {errors.vehicle_type && <p className="text-red-500 text-sm mt-1">{errors.vehicle_type.message}</p>}
          </div>

          {/* Column I: Vehicle Number */}
          <div>
            <Label htmlFor="vehicle_number" className="text-black font-medium">Vehicle Number</Label>
            <Input
              id="vehicle_number"
              data-testid="reg-vehicle-input"
              {...register("vehicle_number")}
              onChange={handlePlateChange}
              placeholder="AP31AX1234"
              className="input-clearwayy h-12 text-base rounded-xl mt-1 uppercase-input font-mono tracking-wider"
            />
            {errors.vehicle_number && <p className="text-red-500 text-sm mt-1">{errors.vehicle_number.message}</p>}
          </div>

          {/* Emergency Contact Section - Columns O, P */}
          <div className="pt-4 border-t border-black/10">
            <p className="text-black/70 text-sm font-medium mb-3">Emergency Contact Details</p>
            
            {/* Column O: Emergency Name */}
            <div className="mb-4">
              <Label htmlFor="emergency_name" className="text-black font-medium">Emergency Contact Name</Label>
              <Input
                id="emergency_name"
                data-testid="reg-emergency-name-input"
                {...register("emergency_name")}
                placeholder="Jane Doe"
                className="input-clearwayy h-12 text-base rounded-xl mt-1"
              />
              {errors.emergency_name && <p className="text-red-500 text-sm mt-1">{errors.emergency_name.message}</p>}
            </div>

            {/* Column P: Emergency Number */}
            <div>
              <Label htmlFor="emergency_number" className="text-black font-medium">Emergency Contact Number</Label>
              <div className="relative mt-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-black/60 font-medium text-sm">+91</span>
                <Input
                  id="emergency_number"
                  data-testid="reg-emergency-number-input"
                  {...register("emergency_number")}
                  placeholder="9876543210"
                  maxLength={10}
                  className="input-clearwayy h-12 text-base rounded-xl pl-12"
                />
              </div>
              {errors.emergency_number && <p className="text-red-500 text-sm mt-1">{errors.emergency_number.message}</p>}
            </div>
          </div>

          <button
            type="submit"
            data-testid="reg-submit-btn"
            disabled={isSubmitting || isDisabled}
            className="btn-primary w-full h-14 rounded-full text-lg font-bold uppercase tracking-wider mt-6 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Activating...
              </>
            ) : (
              "Activate Sticker"
            )}
          </button>
        </form>
      </div>
    </motion.div>
  );
};

// Sticker Success Card
const StickerSuccessCard = ({ message, plate, onClose }) => {
  const whatsappLink = `https://wa.me/917032073590?text=Hi%20Clearwayy%2C%20please%20activate%20alerts%20for%20my%20vehicle%20${plate}.`;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="w-full max-w-md mx-auto"
    >
      <div 
        className="glass-card rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
        data-testid="sticker-success-card"
      >
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#34C759] flex items-center justify-center">
          <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path className="checkmark-path" d="M5 12l5 5L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-black text-black mb-2">Almost Done!</h2>
        <p className="text-black/70 text-lg mb-2">
          Your sticker for <span className="font-mono font-bold">{plate}</span> is registered.
        </p>
        <p className="text-black/60 text-sm mb-6">
          To activate WhatsApp alerts, send us a message below.
        </p>
        
        {/* WhatsApp Activation Button */}
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-activate-btn"
          className="w-full h-14 rounded-full text-lg font-bold uppercase tracking-wider flex items-center justify-center gap-3 bg-[#25D366] text-white hover:bg-[#20BD5A] transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Activate WhatsApp Alerts
        </a>
        
        <p className="text-[#FF9500] text-xs font-medium mt-4 px-4">
          Your registration is only active once you send this message.
        </p>
        
        <button
          onClick={onClose}
          data-testid="sticker-success-close-btn"
          className="text-black/50 text-sm font-medium mt-6 hover:text-black transition-colors"
        >
          I'll do this later
        </button>
      </div>
    </motion.div>
  );
};

// Emergency Modal Component
const EmergencyModal = ({ stickerId, onClose }) => {
  const [activeTab, setActiveTab] = useState("authorities"); // authorities, contact
  const [bystanderName, setBystanderName] = useState("");
  const [bystanderPhone, setBystanderPhone] = useState("");
  const [shareLocation, setShareLocation] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  const [locationData, setLocationData] = useState(null);

  // Get location if toggle is on
  useEffect(() => {
    if (shareLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationData({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          console.log('Location error:', error);
          toast.error("Unable to get location. Please enable location access.");
          setShareLocation(false);
        }
      );
    } else {
      setLocationData(null);
    }
  }, [shareLocation]);

  const handleSendAlert = async () => {
    setIsSending(true);
    try {
      // Build location_url BEFORE sending - await GPS if toggle is on
      let location_url = "";
      if (shareLocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
            });
          });
          location_url = `https://www.google.com/maps?q=${pos.coords.latitude},${pos.coords.longitude}`;
        } catch (geoErr) {
          // Permission denied or timeout - send empty string per requirement
          location_url = "";
        }
      }

      const payload = {
        id: stickerId,
        contact_type: 'emergency',
        bystander_name: bystanderName || "Anonymous",
        bystander_phone: bystanderPhone ? `+91${bystanderPhone}` : "",
        location_url,
        timestamp: new Date().toISOString()
      };

      
      // Direct call to Make.com Emergency webhook with CORS handshake
      const EMERGENCY_WEBHOOK = 'https://hook.eu1.make.com/dp8c418oqde552mysml2yhj725lyb5c6';
      await fetch(EMERGENCY_WEBHOOK, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      
      // Save timestamp for localStorage cooldown
      localStorage.setItem(`cw_cooldown_${stickerId}`, String(Date.now()));
      setAlertSent(true);
    } catch (error) {
      console.log('Emergency alert error:', error);
      toast.error("Failed to send alert. Please try calling directly.");
    } finally {
      setIsSending(false);
    }
  };

  if (alertSent) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.15)]" data-testid="emergency-confirmation">
            <div
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#DC2626]/10 flex items-center justify-center"
              style={{ animation: 'emergencyPulse 2s ease-in-out infinite' }}
            >
              <Check className="w-10 h-10 text-[#DC2626]" />
            </div>
            <h2 className="text-2xl font-black text-black mb-3">Emergency Alert Sent</h2>
            <p className="text-black/60 text-sm leading-relaxed px-2 mb-8">
              Emergency alert sent to their emergency contact listed by this car owner. Thank you for being a good samaritan.
            </p>
            <button
              onClick={onClose}
              data-testid="emergency-close-btn"
              className="w-full py-3.5 rounded-full bg-[#DC2626] text-white font-bold text-base hover:bg-[#B91C1C] transition-colors"
            >
              Close
            </button>
          </div>
          <style>{`
            @keyframes emergencyPulse {
              0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.2); }
              50% { box-shadow: 0 0 0 12px rgba(220,38,38,0); }
            }
          `}</style>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="glass-card rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-black">Emergency Actions</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center">
            <X className="w-5 h-5 text-black" />
          </button>
        </div>

        {/* Tab Buttons */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("authorities")}
            className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${
              activeTab === "authorities" 
                ? "bg-[#FF3B30] text-white" 
                : "bg-black/5 text-black"
            }`}
          >
            Contact Authorities
          </button>
          <button
            onClick={() => setActiveTab("contact")}
            className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${
              activeTab === "contact" 
                ? "bg-[#FF3B30] text-white" 
                : "bg-black/5 text-black"
            }`}
          >
            Notify Family
          </button>
        </div>

        {activeTab === "authorities" ? (
          /* Path A: Contact Authorities */
          <div className="space-y-3">
            <p className="text-black/60 text-sm mb-4">Tap to call emergency services:</p>
            
            <a
              href="tel:108"
              data-testid="call-ambulance"
              className="flex items-center gap-4 p-4 rounded-2xl bg-[#FF3B30] text-white active:scale-[0.97] transition-transform shadow-lg"
            >
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Phone className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg">Ambulance</p>
                <p className="text-white/90 text-sm font-medium underline underline-offset-2 decoration-white/50">108 / 102</p>
              </div>
              <ArrowLeft className="w-5 h-5 rotate-180 opacity-60" />
            </a>

            <a
              href="tel:100"
              data-testid="call-police"
              className="flex items-center gap-4 p-4 rounded-2xl bg-[#007AFF] text-white active:scale-[0.97] transition-transform shadow-lg"
            >
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Phone className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg">Police</p>
                <p className="text-white/90 text-sm font-medium underline underline-offset-2 decoration-white/50">100</p>
              </div>
              <ArrowLeft className="w-5 h-5 rotate-180 opacity-60" />
            </a>

            <a
              href="tel:101"
              data-testid="call-fire"
              className="flex items-center gap-4 p-4 rounded-2xl bg-[#FF9500] text-white active:scale-[0.97] transition-transform shadow-lg"
            >
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Phone className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg">Fire</p>
                <p className="text-white/90 text-sm font-medium underline underline-offset-2 decoration-white/50">101</p>
              </div>
              <ArrowLeft className="w-5 h-5 rotate-180 opacity-60" />
            </a>
          </div>
        ) : (
          /* Path B: Notify Emergency Contact */
          <div className="space-y-4">
            <p className="text-black/60 text-sm">Help us notify the owner's emergency contact:</p>
            
            <div>
              <Label className="text-black font-medium">Your Name (Optional)</Label>
              <Input
                value={bystanderName}
                onChange={(e) => setBystanderName(e.target.value)}
                placeholder="Good Samaritan"
                className="input-clearwayy h-12 rounded-xl mt-1"
              />
            </div>

            <div>
              <Label className="text-black font-medium">Your Phone (Optional)</Label>
              <div className="relative mt-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-black/60 font-medium text-sm">+91</span>
                <Input
                  value={bystanderPhone}
                  onChange={(e) => setBystanderPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="9876543210"
                  maxLength={10}
                  className="input-clearwayy h-12 rounded-xl pl-12"
                />
              </div>
            </div>

            {/* Location Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-black/5">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-black" />
                <div>
                  <p className="font-medium text-black text-sm">Share Location</p>
                  <p className="text-black/50 text-xs">Help family find the spot</p>
                </div>
              </div>
              <button
                onClick={() => setShareLocation(!shareLocation)}
                className={`w-12 h-7 rounded-full transition-all ${
                  shareLocation ? "bg-[#34C759]" : "bg-black/20"
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform ${
                  shareLocation ? "translate-x-6" : "translate-x-1"
                }`} />
              </button>
            </div>
            
            {shareLocation && locationData && (
              <p className="text-[#34C759] text-xs">✓ Location captured</p>
            )}

            <button
              onClick={handleSendAlert}
              disabled={isSending}
              className="w-full h-14 rounded-full bg-[#FF3B30] text-white text-lg font-bold uppercase tracking-wider flex items-center justify-center gap-2 mt-4"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Alert"
              )}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Already Notified Card with countdown timer
const AlreadyNotifiedCard = ({ stickerId, cooldownMinutes, lastNotifyTime, onReset, onTransition }) => {
  const [minsLeft, setMinsLeft] = useState(cooldownMinutes);

  // Tick countdown every 30s using dynamic cooldownMinutes from backend
  useEffect(() => {
    setMinsLeft(cooldownMinutes);
    const iv = setInterval(() => {
      setMinsLeft((prev) => {
        const next = prev - 0.5; // decrement by 0.5 each 30s tick
        if (next <= 0) {
          // Auto-unlock: transition back to idle so user can retry
          onTransition();
          return 0;
        }
        return Math.ceil(next);
      });
    }, 30000);
    return () => clearInterval(iv);
  }, [cooldownMinutes, onTransition]);

  // Auto-transition to polling screen after 15s
  useEffect(() => {
    const t = setTimeout(onTransition, 15000);
    return () => clearTimeout(t);
  }, [onTransition]);

  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Polling runs underneath */}
      <div className="opacity-0 pointer-events-none absolute inset-0">
        <StatusPollingScreen stickerId={stickerId} type="standard" onReset={onReset} />
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="glass-card rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)]" data-testid="already-notified-card">
          <div className="text-center py-5">
            <div
              className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#4F46E5] flex items-center justify-center"
              style={{ animation: 'gentlePulseIndigo 2.5s ease-in-out infinite' }}
            >
              <Check className="w-10 h-10 text-white" />
            </div>

            <p className="text-xl font-bold text-black leading-snug">
              Great news—the owner was recently alerted!
            </p>

            <p className="text-black/60 mt-3 text-sm leading-relaxed px-2">
              A notification was recently sent. Please wait {minsLeft} more minute{minsLeft !== 1 ? 's' : ''} before trying again.
            </p>

            {/* Dynamic countdown */}
            <div className="mt-4 bg-[#4F46E5]/10 rounded-xl py-2.5 px-4 inline-block">
              <p className="text-[#4F46E5] text-sm font-bold">
                {minsLeft > 0
                  ? `You can send another alert in ${minsLeft} minute${minsLeft !== 1 ? 's' : ''}`
                  : 'You can send another alert now'}
              </p>
            </div>

            {/* Progress bar */}
            <div className="mt-5 mx-4">
              <div className="h-1.5 bg-black/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#4F46E5] rounded-full"
                  style={{ animation: 'progressFill 15s linear forwards' }}
                />
              </div>
              <div className="flex items-center justify-center gap-2 mt-3">
                <Loader2 className="w-3.5 h-3.5 text-[#4F46E5] animate-spin" />
                <p className="text-[#4F46E5] text-xs font-medium">Loading status page...</p>
              </div>
            </div>

            {/* Footer */}
            <p className="text-black/40 text-xs mt-5">
              Please stay on this page for the latest information.
            </p>
          </div>
        </div>
      </motion.div>

      <style>{`
        @keyframes progressFill {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes gentlePulseIndigo {
          0%, 100% { box-shadow: 0 0 0 0 rgba(79,70,229,0.3); }
          50% { box-shadow: 0 0 0 10px rgba(79,70,229,0); }
        }
      `}</style>
    </div>
  );
};


// Notify Owner UI (Existing User from QR scan)
const NotifyOwnerUI = ({ stickerId, vehiclePlate, ownerName, onSuccess }) => {
  const [notifyState, setNotifyState] = useState("idle");
  const [isDisabled, setIsDisabled] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [lastNotifyTime, setLastNotifyTime] = useState(null);
  const [cooldownMinutes, setCooldownMinutes] = useState(10);
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Check localStorage cooldown on mount
  useEffect(() => {
    const stored = localStorage.getItem(`cw_cooldown_${stickerId}`);
    if (stored) {
      const ts = parseInt(stored, 10);
      const elapsedMin = (Date.now() - ts) / 60000;
      if (elapsedMin < 15) {
        // Still in cooldown — go straight to polling screen without hitting API
        setCooldownMinutes(Math.ceil(15 - elapsedMin));
        setLastNotifyTime(new Date(ts));
        setNotifyState("sent"); // Skip to polling screen
      } else {
        localStorage.removeItem(`cw_cooldown_${stickerId}`);
      }
    }
  }, [stickerId]);

  // Initialize Turnstile widget
  useEffect(() => {
    let checkInterval = null;
    
    const initTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: "0x4AAAAAAC0F_OtIeEHUZ8C7",
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(null),
          "error-callback": () => setTurnstileToken(null),
          theme: "light",
        });
      }
    };

    if (window.turnstile) {
      initTurnstile();
    } else {
      checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval);
          initTurnstile();
        }
      }, 100);
    }

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleNotify = async () => {
    if (isDisabled) return;
    
    if (!turnstileToken) {
      toast.error("Please complete the verification first.");
      return;
    }

    setIsDisabled(true);
    setNotifyState("notifying");

    try {
      
      // Direct call to Make.com Notify webhook
      const NOTIFY_WEBHOOK = 'https://hook.eu1.make.com/u8d88jidvplgsr1x7jy6oy8go5v9fi65';
      const response = await fetch(NOTIFY_WEBHOOK, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: stickerId,
          captcha_token: turnstileToken,
        }),
      });
      
      // Robust parsing: Make.com may return plain text "Accepted" or JSON
      let data = {};
      const responseText = await response.text();
      
      try {
        data = JSON.parse(responseText);
      } catch {
        // Not JSON — if "Accepted" or 200, treat as success
        data = { status: responseText.trim().toLowerCase() === 'accepted' ? 'success' : responseText };
      }
      

      if (data.status === 'already_notified') {
        // Read dynamic timer from backend
        const minutesLeft = parseInt(data.minutes_left, 10);
        const dynamicMinutes = isNaN(minutesLeft) ? 10 : minutesLeft;
        
        if (dynamicMinutes <= 0) {
          setNotifyState("idle");
          setIsDisabled(false);
          toast.success("You can send another notification now.");
        } else {
          setCooldownMinutes(dynamicMinutes);
          setLastNotifyTime(data.last_notify_time ? new Date(data.last_notify_time) : new Date());
          setNotifyState("already_notified");
        }
      } else if (data.status === 'limit_reached') {
        // Kill switch — persistent limit screen
        setNotifyState("limit_reached");
      } else {
        // Success — save timestamp to localStorage for 15-min lock
        localStorage.setItem(`cw_cooldown_${stickerId}`, String(Date.now()));
        setTimeout(() => setNotifyState("sent"), 2000);
      }
    } catch (error) {
      console.log('Notify Error:', error);
      setNotifyState("error");
      toast.error("Failed to notify owner. Please try again.");
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken(null);
      }
    } finally {
      setTimeout(() => setIsDisabled(false), 5000);
    }
  };

  // When sent, show polling screen (replaces the entire card)
  if (notifyState === "sent") {
    return (
      <StatusPollingScreen
        stickerId={stickerId}
        type="standard"
        onReset={() => setNotifyState("idle")}
      />
    );
  }

  // Kill switch — service limit reached
  if (notifyState === "limit_reached") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-auto"
      >
        <div className="glass-card rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="limit-reached-card">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#DC2626]/10 flex items-center justify-center">
            <Shield className="w-10 h-10 text-[#DC2626]" />
          </div>
          <h2 className="text-xl font-bold text-black mb-3">Service Limit Reached</h2>
          <p className="text-black/50 text-sm leading-relaxed mb-6">
            This vehicle ID has reached its notification limit. Please contact{' '}
            <a href="mailto:support@clearwayy.in" className="text-[#FF6B00] font-bold underline">support@clearwayy.in</a>{' '}
            to inform the owner.
          </p>
          <a
            href="mailto:support@clearwayy.in"
            className="btn-primary inline-block w-full py-3.5 rounded-full text-sm font-bold text-center"
          >
            Contact Support
          </a>
        </div>
      </motion.div>
    );
  }

  // Cooldown: owner was already notified recently
  // Polling runs in the background so green banner can appear immediately
  if (notifyState === "already_notified") {
    return (
      <AlreadyNotifiedCard
        stickerId={stickerId}
        cooldownMinutes={cooldownMinutes}
        lastNotifyTime={lastNotifyTime}
        onReset={() => setNotifyState("idle")}
        onTransition={() => setNotifyState("sent")}
      />
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-auto"
      >
        <div 
          className="glass-card rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
          data-testid="notify-owner-ui"
        >
          {notifyState === "notifying" ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-black flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-[#FF6B00] animate-spin" />
              </div>
              <p className="text-xl font-bold text-black">Sending notification...</p>
              <p className="text-black/50 mt-2 text-sm">Notification has been sent. We will update you here as soon as the owner responds.</p>
            </div>
          ) : notifyState === "error" ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#FF3B30] flex items-center justify-center">
                <X className="w-10 h-10 text-white" />
              </div>
              <p className="text-xl font-bold text-black">Failed to Send</p>
              <p className="text-black/60 mt-2">Please try again</p>
              <button onClick={() => setNotifyState("idle")} className="mt-4 text-[#007AFF] font-medium">
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-black flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-[#FF6B00]" />
                </div>
                <h2 className="text-xl font-bold text-black">Blocked by this vehicle?</h2>
                {vehiclePlate && (
                  <p className="text-2xl font-mono font-bold text-black mt-3 tracking-wider">
                    {vehiclePlate}
                  </p>
                )}
                {ownerName && (
                  <p className="text-black/60 text-sm mt-1">Owner: {ownerName}</p>
                )}
              </div>

              {/* Turnstile Widget */}
              <div className="flex justify-center mb-4">
                <div ref={turnstileRef} data-testid="turnstile-widget"></div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleNotify}
                  data-testid="notify-owner-btn"
                  disabled={isDisabled || !turnstileToken}
                  className={`btn-primary w-full h-14 rounded-full text-lg font-bold uppercase tracking-wider flex items-center justify-center gap-2 ${
                    !turnstileToken ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <AlertTriangle className="w-5 h-5" />
                  Notify Owner
                </button>

                {/* Emergency Button - High Visibility Red */}
                <button
                  onClick={() => setShowEmergencyModal(true)}
                  data-testid="emergency-btn"
                  className="w-full h-14 rounded-full bg-[#FF3B30] text-white text-lg font-bold uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg hover:bg-[#E6352B] transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  EMERGENCY
                </button>
              </div>
              
              <p className="text-center text-black/50 text-xs mt-4">
                Owner will receive a WhatsApp message
              </p>
            </>
          )}
        </div>
      </motion.div>

      {/* Emergency Modal */}
      {showEmergencyModal && (
        <EmergencyModal 
          stickerId={stickerId} 
          onClose={() => setShowEmergencyModal(false)} 
        />
      )}
    </>
  );
};

// Loading State
const LoadingState = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="w-full max-w-md mx-auto"
  >
    <div className="glass-card rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-black/10 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-black animate-spin" />
      </div>
      <p className="text-lg font-medium text-black">Checking sticker...</p>
    </div>
  </motion.div>
);

// Original Two-Path Interface (No ID parameter)
const TwoPathInterface = ({ onRegister, onNotify }) => (
  <div className="w-full max-w-md mx-auto space-y-4">
    <PathCard
      title="I am a New Owner"
      icon={UserPlus}
      variant="light"
      onClick={onRegister}
      testId="new-owner-card"
    />
    <PathCard
      title="I am Blocked"
      icon={AlertTriangle}
      variant="dark"
      onClick={onNotify}
      testId="blocked-card"
    />
  </div>
);

// Original Registration Form (for non-QR flow)
const OriginalRegistrationForm = ({ onBack, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  
  const registrationSchema = z.object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    city: z.string().min(2, "City must be at least 2 characters"),
    phone: z.string().regex(/^[0-9]{10}$/, "Phone must be exactly 10 digits"),
    email: z.string().email("Invalid email address"),
    plate: z.string().regex(PLATE_REGEX, "Invalid Indian vehicle plate format (e.g., AP31AX1234)"),
    consent: z.boolean().refine((val) => val === true, "You must agree to receive alerts"),
  });
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(registrationSchema),
    defaultValues: { consent: false }
  });

  const consent = watch("consent");

  const handlePlateChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setValue("plate", value);
  };

  const onSubmit = async (data) => {
    if (isDisabled) return;
    
    setIsSubmitting(true);
    setIsDisabled(true);

    try {
      const payload = {
        name: data.name,
        city: data.city,
        phone: `+91${data.phone}`,
        email: data.email,
        plate: data.plate,
        timestamp: new Date().toISOString(),
      };

      await fetch("https://hook.eu1.make.com/gw99frjs9q0q0u2yviq07fch9dfmmi2a", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        mode: "no-cors",
      });

      onSuccess();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setIsDisabled(false), 5000);
    }
  };

  return (
    <motion.div
      initial={{ y: "100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "100%", opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/20"
    >
      <div 
        className="form-container w-full max-w-md mx-auto rounded-t-3xl sm:rounded-3xl p-6 pb-8 max-h-[90vh] overflow-y-auto"
        data-testid="registration-form"
      >
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={onBack}
            data-testid="back-button"
            className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>
          <h2 className="text-xl font-bold text-black">Register Vehicle</h2>
          <div className="w-10" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-black font-medium">Full Name</Label>
            <Input
              id="name"
              data-testid="name-input"
              {...register("name")}
              placeholder="John Doe"
              className="input-clearwayy h-14 text-lg rounded-xl mt-1"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
          </div>

          <div>
            <Label htmlFor="city" className="text-black font-medium">City</Label>
            <Input
              id="city"
              data-testid="city-input"
              {...register("city")}
              placeholder="Hyderabad"
              className="input-clearwayy h-14 text-lg rounded-xl mt-1"
            />
            {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city.message}</p>}
          </div>

          <div>
            <Label htmlFor="phone" className="text-black font-medium">WhatsApp Number</Label>
            <div className="relative mt-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-black/60 font-medium">+91</span>
              <Input
                id="phone"
                data-testid="phone-input"
                {...register("phone")}
                placeholder="9876543210"
                maxLength={10}
                className="input-clearwayy h-14 text-lg rounded-xl pl-14"
              />
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
          </div>

          <div>
            <Label htmlFor="email" className="text-black font-medium">Email</Label>
            <Input
              id="email"
              data-testid="email-input"
              {...register("email")}
              type="email"
              placeholder="john@example.com"
              className="input-clearwayy h-14 text-lg rounded-xl mt-1"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <Label htmlFor="plate" className="text-black font-medium">Vehicle Number</Label>
            <Input
              id="plate"
              data-testid="plate-input"
              {...register("plate")}
              onChange={handlePlateChange}
              placeholder="AP31AX1234"
              className="input-clearwayy h-14 text-lg rounded-xl mt-1 uppercase-input font-mono tracking-wider"
            />
            {errors.plate && <p className="text-red-500 text-sm mt-1">{errors.plate.message}</p>}
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="consent"
              data-testid="consent-checkbox"
              checked={consent}
              onCheckedChange={(checked) => setValue("consent", checked)}
              className="mt-0.5 border-black/30 data-[state=checked]:bg-black data-[state=checked]:border-black"
            />
            <Label htmlFor="consent" className="text-sm text-black/70 leading-relaxed cursor-pointer">
              I agree to receive parking alerts via WhatsApp.
            </Label>
          </div>
          {errors.consent && <p className="text-red-500 text-sm">{errors.consent.message}</p>}

          <button
            type="submit"
            data-testid="register-submit-btn"
            disabled={isSubmitting || isDisabled}
            className="btn-primary w-full h-14 rounded-full text-lg font-bold uppercase tracking-wider mt-6 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Registering...
              </>
            ) : (
              "Activate CLEARWAYY"
            )}
          </button>
        </form>
      </div>
    </motion.div>
  );
};

// Original Notify Form (for non-QR flow)
const OriginalNotifyForm = ({ onBack, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [notifyState, setNotifyState] = useState("idle");

  const notifySchema = z.object({
    plate: z.string().regex(PLATE_REGEX, "Invalid Indian vehicle plate format (e.g., AP31AX1234)"),
  });

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(notifySchema),
  });

  const handlePlateChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setValue("plate", value);
  };

  const onSubmit = async (data) => {
    if (isDisabled) return;

    setIsSubmitting(true);
    setIsDisabled(true);
    setNotifyState("notifying");

    try {
      const payload = {
        action: "notify",
        plate: data.plate,
        timestamp: new Date().toISOString(),
      };

      await fetch("https://hook.eu1.make.com/gw99frjs9q0q0u2yviq07fch9dfmmi2a", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        mode: "no-cors",
      });

      setTimeout(() => {
        setNotifyState("sent");
        setTimeout(() => onSuccess(), 1500);
      }, 2000);
    } catch (error) {
      setNotifyState("limit");
      toast.error("Limit Reached. Try again later.");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setIsDisabled(false), 5000);
    }
  };

  return (
    <motion.div
      initial={{ y: "100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "100%", opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/20"
    >
      <div 
        className="form-container w-full max-w-md mx-auto rounded-t-3xl sm:rounded-3xl p-6 pb-8"
        data-testid="notify-form"
      >
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={onBack}
            data-testid="notify-back-button"
            className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>
          <h2 className="text-xl font-bold text-black">Notify Owner</h2>
          <div className="w-10" />
        </div>

        {notifyState === "notifying" ? (
          <div className="text-center py-12 animate-pulse-notify" data-testid="notifying-state">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-black flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#FF6B00] animate-spin" />
            </div>
            <p className="text-xl font-bold text-black">Notifying Owner...</p>
            <p className="text-black/60 mt-2">Please wait</p>
          </div>
        ) : notifyState === "sent" ? (
          <div className="text-center py-12" data-testid="sent-state">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#34C759] flex items-center justify-center">
              <Check className="w-10 h-10 text-white" />
            </div>
            <p className="text-xl font-bold text-black">Message Sent</p>
            <p className="text-black/60 mt-2">The owner has been notified</p>
          </div>
        ) : notifyState === "limit" ? (
          <div className="text-center py-12" data-testid="limit-state">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#FF3B30] flex items-center justify-center">
              <X className="w-10 h-10 text-white" />
            </div>
            <p className="text-xl font-bold text-black">Limit Reached</p>
            <p className="text-black/60 mt-2">Please try again later</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="notify-plate" className="text-black font-medium">
                Enter Blocking Vehicle Number
              </Label>
              <Input
                id="notify-plate"
                data-testid="notify-plate-input"
                {...register("plate")}
                onChange={handlePlateChange}
                placeholder="AP31AX1234"
                className="input-clearwayy h-14 text-lg rounded-xl mt-1 uppercase-input font-mono tracking-wider text-center"
              />
              {errors.plate && <p className="text-red-500 text-sm mt-1">{errors.plate.message}</p>}
            </div>

            <button
              type="submit"
              data-testid="notify-submit-btn"
              disabled={isSubmitting || isDisabled}
              className="btn-primary w-full h-14 rounded-full text-lg font-bold uppercase tracking-wider mt-6 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                "Notify Owner"
              )}
            </button>
          </form>
        )}
      </div>
    </motion.div>
  );
};

// Original Success Card
const OriginalSuccessCard = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-6"
  >
    <div 
      className="glass-card w-full max-w-md rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
      data-testid="success-card"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#34C759] flex items-center justify-center">
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path className="checkmark-path" d="M5 12l5 5L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-black text-black mb-2">CLEARWAYY Activated</h2>
      <p className="text-black/60 text-lg">Your path is now clear.</p>
      
      <button
        onClick={onClose}
        data-testid="success-close-btn"
        className="btn-primary w-full h-14 rounded-full text-lg font-bold uppercase tracking-wider mt-8"
      >
        Done
      </button>
    </div>
  </motion.div>
);

// Main Home Component with URL Parameter Logic
const Home = () => {
  const [isNotifyView, setIsNotifyView] = useState(false);
  const [stickerId, setStickerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stickerData, setStickerData] = useState(null);
  const [view, setView] = useState("home");
  const [successMessage, setSuccessMessage] = useState("");
  const [successPlate, setSuccessPlate] = useState("");

  // Simple useEffect - check URL and fetch data
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    
    
    if (id) {
      
      setStickerId(id);
      setIsNotifyView(true);
      // Stay in loading state — never default to any form
      
      // Supabase RPC: get_sticker returns {secret_id, is_registered, live_status, status_timestamp, vehicle_number, vehicle_type, owner_first_name}
      supabase.rpc('get_sticker', { p_id: id })
        .then(({ data, error }) => {
          if (error) { console.log('RPC get_sticker error:', error); }
          
          const row = Array.isArray(data) ? data[0] : data;
          
          if (error || !row) {
            // No row found → invalid ID
            setStickerData({ status: 'invalid_id' });
            setLoading(false);
            return;
          }
          
          // Check is_registered flag from RPC
          if (row.is_registered) {
            // Registered → Notify Owner screen
            setStickerData({
              isRegistered: true,
              has_phone: true,
              plate: (row.vehicle_number || '').trim(),
              ownerName: (row.owner_first_name || '').trim(),
            });
          } else {
            // Not registered — show Registration Form
            setStickerData({ isRegistered: false, has_phone: false });
          }
          setLoading(false);
        })
        .catch((err) => {
          console.log('RPC check error:', err);
          setStickerData({ status: 'connection_error' });
          setLoading(false);
        });
    } else {
      setIsNotifyView(false);
      setLoading(false);
    }
  }, []);

  // Log state changes
  useEffect(() => {
  }, [isNotifyView, stickerData]);

  const handleBack = useCallback(() => setView("home"), []);
  const handleSuccess = useCallback(() => setView("success"), []);
  const handleClose = useCallback(() => setView("home"), []);
  const handleStickerSuccess = useCallback((message, plate) => {
    setSuccessMessage(message);
    setSuccessPlate(plate);
    setView("stickerSuccess");
  }, []);

  // ============================================
  // NOTIFY VIEW - When ID is present in URL
  // ============================================
  const NotifyViewContent = () => {
    if (loading) {
      return <LoadingState />;
    }

    // Show success card after registration (preserves WhatsApp activation flow)
    if (view === "stickerSuccess") {
      return <StickerSuccessCard message={successMessage} plate={successPlate} onClose={handleClose} />;
    }

    if (stickerData) {
      // INVALID ID → Oops screen
      if (stickerData.status === 'invalid_id') {
        return <NoIdError />;
      }

      // CONNECTION ERROR → timeout/CORS error screen
      if (stickerData.status === 'connection_error') {
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md mx-auto"
          >
            <div className="glass-card rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-black/5 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-black/30" />
              </div>
              <h2 className="text-xl font-bold text-black mb-2" data-testid="connection-error-title">Connection timed out</h2>
              <p className="text-black/50 text-sm leading-relaxed mb-6">
                We couldn't reach the server. Please check your connection and try again.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  data-testid="retry-btn"
                  className="btn-primary w-full py-3 rounded-full text-sm font-bold"
                >
                  Refresh & Retry
                </button>
                {stickerData.handshakeUrl && (
                  <button
                    onClick={() => {
                      window.open(stickerData.handshakeUrl, '_blank');
                      setTimeout(() => window.location.reload(), 3000);
                    }}
                    data-testid="handshake-btn"
                    className="w-full py-3 rounded-full text-sm font-bold border-2 border-black/10 text-black/70 hover:border-black/30 transition-colors"
                  >
                    Security Handshake
                  </button>
                )}
              </div>
              <p className="text-black/30 text-xs mt-4">
                If Refresh doesn't work, tap Security Handshake to authorize the connection.
              </p>
            </div>
          </motion.div>
        );
      }

      // IF isRegistered=true: Show Notify Owner dashboard
      if (stickerData.isRegistered === true) {
        return (
          <NotifyOwnerUI
            stickerId={stickerId}
            vehiclePlate={stickerData.plate}
            ownerName={stickerData.ownerName}
            onSuccess={handleClose}
          />
        );
      }
      
      // IF isRegistered=false: Show Registration Form
      return (
        <StickerRegistrationForm
          stickerId={stickerId}
          onSuccess={handleStickerSuccess}
        />
      );
    }

    return <LoadingState />;
  };

  // ============================================
  // HOME VIEW - When no ID in URL → Landing Page
  // ============================================
  const HomeViewContent = () => (
    <LandingPage />
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  // No ID → show the premium landing page (full screen, no wrapper)
  if (!isNotifyView && !loading) {
    return <HomeViewContent />;
  }

  // QR scan flow — white background + logo header
  return (
    <div className="clearwayy-bg min-h-screen relative" data-testid="clearwayy-home">
      <VideoBackground />
      
      <div className="relative z-10 min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-1 flex flex-col justify-center px-6 pb-12">
          <NotifyViewContent />
        </main>

        <footer className="text-center pb-8 px-6">
          <p className="text-sm text-black/30">
            &copy; {new Date().getFullYear()} Clearwayy
          </p>
        </footer>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#000000',
            color: '#FF6B00',
            border: 'none',
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/scan" element={<Home />} />
          <Route path="/partner-dashboard" element={<PartnerDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
